import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    MaterialReactTable,
    MRT_GlobalFilterTextField,
    MRT_ToggleFiltersButton,
    MRT_ToolbarAlertBanner
} from 'material-react-table';
import {MRT_Localization_RU} from 'material-react-table/locales/ru';
import Box from '@mui/material/Box';
import {Link, useLocation} from 'react-router-dom';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import DateRangeFilter from '../../../../filter/DateRangeFilter';
import ModalAlert from '../../../customer-detales/ModalAlert';
import {constantsErrorMessages} from '../../../../../../Constants';
import {requestsCuttingStatuses} from '../../../../../../data/RequestsCuttingState';
import {EditLocalStoragePagesData} from '../../../../../../utils/EditLocalStoragePagesData';
import {calcFilterData} from '../../../../../../utils/common/calcFilterData';
import OrderUtil from '../../../../../../utils/orders/OrderUtil';
import {RequestsCuttingFilterData} from '../../../../../../data/FilterTables/RequestsCuttingFilterData';
import {
    enrichRequestsCuttingRows,
    getRequestsCuttingMaterialName,
} from '../../../../../../utils/orders/RequestsCuttingUtil';
import CompositionRequestsCuttingService
    from '../../../../../../API/orders/optimization/organization/CompositionRequestsCuttingService';

/**
 * Таблица заявок на раскрой только для карточки заказа-композиции.
 * Не используется в cloud / клиентских / общих списках заявок.
 */
const CompositionRequestsCuttingTable = ({orderId, handleModalAlert: parentHandleModalAlert}) => {
    const pathname = useLocation().pathname;
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [globalFilter, setGlobalFilter] = useState('');
    const [columnsFilter, setColumnsFilter] = useState([]);
    const [showColumnFilters, setShowColumnFilters] = useState(false);
    const [statusList, setStatusList] = useState([]);
    const [alertData, setAlertData] = useState({});
    const [tableOptions, setTableOptions] = useState({
        pageIndex: 0,
        pageSize: 50,
        totalElements: 0,
        searchData: '',
    });

    const handleModalAlert = (message, type = 'error') => {
        if (parentHandleModalAlert) {
            parentHandleModalAlert(message, type);
            return;
        }
        setAlertData({
            type,
            open: true,
            message: type === 'success'
                ? message
                : message.response?.data?.message || constantsErrorMessages[message.response?.status] || message.message,
        });
    };

    const tableOptionsHandle = (nextState) => {
        setTableOptions(nextState);
        if (nextState.pageSize !== tableOptions.pageSize) {
            EditLocalStoragePagesData(pathname, 'pageSize', nextState.pageSize);
        }
    };

    const handleSearchQuery = (searchQuery, prevData, pageSize, isColumnFilters) => {
        const newColumnsFilter = isColumnFilters ? searchQuery : columnsFilter;
        const newGlobalFilter = isColumnFilters ? globalFilter : searchQuery || '';
        const rows = [...prevData];
        const globalFilterData = newGlobalFilter
            ? calcFilterData(rows, newGlobalFilter, RequestsCuttingFilterData)
            : rows;
        const newData = newColumnsFilter.reduce((accumulator, currentValue) => {
            return calcFilterData(
                accumulator,
                currentValue?.value,
                [RequestsCuttingFilterData.find((el) => el.idFromColumnFilter === currentValue.id)],
            );
        }, globalFilterData);

        setFilteredData(newData);
        tableOptionsHandle({
            ...tableOptions,
            totalElements: newData.length,
            pageSize: pageSize ? pageSize : tableOptions?.pageSize,
        });

        if (isColumnFilters) {
            setColumnsFilter(searchQuery);
        } else {
            setGlobalFilter(searchQuery || '');
            if (searchQuery !== globalFilter) {
                EditLocalStoragePagesData(pathname, 'searchQuery', searchQuery);
            }
        }
    };

    const getStartPageSize = () => {
        const pagesData = JSON.parse(localStorage.getItem('pagesData')) || {};
        return {
            pageSize: pagesData[pathname]?.pageSize,
            searchQuery: pagesData[pathname]?.searchQuery,
        };
    };

    useEffect(() => {
        const {searchQuery, pageSize} = getStartPageSize();
        setIsLoading(true);
        CompositionRequestsCuttingService.getAllByOrderId(orderId)
            .then((response) => {
                const rows = enrichRequestsCuttingRows(response.data);
                setData(rows);
                handleSearchQuery(searchQuery, rows, pageSize);
                setStatusList(OrderUtil.getListFieldByEnum(rows, 'requestsCuttingState', requestsCuttingStatuses));
            })
            .catch((error) => {
                handleModalAlert(error);
            })
            .finally(() => setIsLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId]);

    const handleDeleteRow = useCallback((row) => {
        CompositionRequestsCuttingService.delete(row.id)
            .then(() => {
                const newData = data.filter((el) => el.id !== row.id);
                setData(newData);
                handleSearchQuery(globalFilter, newData);
                handleModalAlert('Данные удалены', 'success');
            })
            .catch((error) => {
                handleModalAlert(error);
            });
    }, [data, globalFilter]);

    const handleShowColumnFilters = () => {
        setShowColumnFilters(!showColumnFilters);
        if (showColumnFilters) {
            handleSearchQuery([], data, null, true);
        }
    };

    const columns = useMemo(() => [
        {
            id: 'number',
            header: 'Номер',
            accessorKey: 'number',
            minSize: 8,
            maxSize: 8,
            Cell: ({cell, row}) => (
                <span style={{color: row.original.placeholder ? 'red' : 'black'}}>
                    {cell.getValue()}
                </span>
            ),
        },
        {
            id: 'materialName',
            header: 'материал',
            accessorFn: (row) => getRequestsCuttingMaterialName(row),
            minSize: 150,
            maxSize: 300,
            Cell: ({cell, row}) => (
                <li>
                    <Link
                        to={`/organization/requests-cutting/details/${row.original.id}/${encodeURIComponent(row.original.number)}/composition?orderId=${orderId}`}
                    >
                        {cell.getValue() ?? ''}
                    </Link>
                </li>
            ),
        },
        {
            id: 'thickness',
            header: 'Толщина',
            accessorFn: (row) => row.thickness ?? row.materialOrganizationOption?.thickness ?? null,
            minSize: 8,
            maxSize: 8,
        },
        {
            accessorFn: (row) => (row.dateOfReceipt == null ? undefined : new Date(row.dateOfReceipt)),
            id: 'date',
            header: 'Дата поступления',
            sortingFn: 'datetime',
            size: 10,
            Cell: ({cell}) => (cell.getValue() !== undefined ? cell.getValue().toLocaleDateString() : null),
            Filter: ({column}) => <DateRangeFilter column={column}/>,
        },
        {
            id: 'requestsCuttingState',
            header: 'Статус',
            accessorKey: 'requestsCuttingState',
            minSize: 55,
            maxSize: 55,
            filterVariant: 'multi-select',
            filterSelectOptions: statusList,
            Cell: ({cell}) => requestsCuttingStatuses[cell.getValue()],
            filterFn: (row, id, filterValue) =>
                requestsCuttingStatuses[row.original.requestsCuttingState]
                    ?.toLowerCase()
                    .includes(filterValue.toLowerCase()),
            muiFilterTextFieldProps: {
                placeholder: 'Отфильтровать по статус',
                sx: {width: '350px'},
            },
        },
        {
            header: 'id',
            accessorKey: 'id',
            enableColumnActions: false,
            enableGrouping: false,
            enableEditing: false,
            size: 2,
            maxSize: 2,
        },
    ], [statusList]);

    return (
        <div>
            {isLoading ? (
                <Box sx={{
                    height: 'calc(100vh - 435px)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                    <CircularProgress disableShrink/>
                </Box>
            ) : (
                <MaterialReactTable
                    renderTopToolbar={({table}) => (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '7px',
                            backgroundColor: 'transparent',
                        }}>
                            <MRT_ToolbarAlertBanner table={table}/>
                            <Box sx={{display: 'flex'}}>
                                <MRT_ToggleFiltersButton table={table}/>
                                <MRT_GlobalFilterTextField table={table}/>
                            </Box>
                        </Box>
                    )}
                    muiTableBodyCellProps={{
                        sx: {
                            p: '0px 0px 0px 10px',
                            m: 0,
                            border: 'none',
                            '&:after': {display: 'none'},
                            '&:not(:last-of-type)': {borderRight: '1px solid #EAEBEA'},
                            '& li': {
                                listStyleType: 'none',
                                '& a': {
                                    color: '#006CCC',
                                    textDecoration: 'none',
                                    '&:hover': {textDecoration: 'underline'},
                                },
                            },
                        },
                    }}
                    muiTableHeadCellProps={{
                        sx: {
                            border: '1px solid #EAEBEA',
                            backgroundColor: '#F5F6FA',
                            padding: '10px',
                            paddingBottom: '0.2rem',
                            paddingTop: '0.2rem',
                        },
                    }}
                    muiTableBodyRowProps={() => ({
                        sx: (theme) => ({
                            height: '36px',
                            backgroundColor: 'white',
                            '&:hover': {backgroundColor: theme.palette.primary.stroke},
                            '&:nth-of-type(even)': {
                                backgroundColor: theme.palette.primary.lightGrey,
                                '&:hover': {backgroundColor: theme.palette.primary.stroke},
                            },
                        }),
                    })}
                    displayColumnDefOptions={{
                        'mrt-row-actions': {header: '', size: 3, minSize: 3, grow: false},
                    }}
                    muiSearchTextFieldProps={{
                        placeholder: 'Поиск',
                        sx: {
                            paddingTop: '5px',
                            paddingBottom: '0px',
                            '& .MuiCollapse-root': {
                                borderRadius: '12px',
                                height: 40,
                                width: 290,
                            },
                        },
                        variant: 'outlined',
                    }}
                    columns={columns}
                    data={filteredData}
                    initialState={{
                        columnVisibility: {id: false},
                        density: 'compact',
                        pagination: {pageIndex: 0, pageSize: 50},
                    }}
                    getRowId={(originalRow) => originalRow.id}
                    onPaginationChange={(updater) => {
                        tableOptionsHandle(updater(tableOptions));
                    }}
                    onGlobalFilterChange={(value) => {
                        handleSearchQuery(value, data, null);
                    }}
                    onColumnFiltersChange={(updater) => {
                        handleSearchQuery(updater(columnsFilter), data, null, true);
                    }}
                    onShowColumnFiltersChange={handleShowColumnFilters}
                    state={{
                        showColumnFilters,
                        columnFilters: columnsFilter,
                        globalFilter,
                        showGlobalFilter: true,
                        pagination: tableOptions,
                    }}
                    manualFiltering={true}
                    rowCount={tableOptions.totalElements}
                    enableRowSelection={false}
                    muiTableContainerProps={{
                        sx: {maxHeight: 'calc(100vh - 435px)'},
                    }}
                    renderRowActionMenuItems={({closeMenu, row}) => [
                        <MenuItem
                            key="delete"
                            disabled={row.original.requestsCuttingState !== 'CALCULATION'}
                            onClick={() => {
                                handleDeleteRow(row.original);
                                closeMenu();
                            }}
                            sx={{
                                margin: '0 8px',
                                borderRadius: '4px',
                                '&:hover': {backgroundColor: (theme) => theme.palette.primary.light},
                            }}
                        >
                            Удалить
                        </MenuItem>,
                    ]}
                    localization={MRT_Localization_RU}
                    enableRowActions={true}
                    enableToolbarInternalActions={false}
                />
            )}
            {!parentHandleModalAlert && (
                <ModalAlert
                    data={alertData}
                    onClose={() => setAlertData((alert) => ({...alert, open: false}))}
                />
            )}
        </div>
    );
};

export default CompositionRequestsCuttingTable;
